import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { UnifiedLoadingScreen } from '@/components/UnifiedLoadingScreen';

interface LoadingStep {
  id: string;
  message: string;
  isActive: boolean;
}

interface LoadingContextValue {
  addLoadingStep: (id: string, message: string) => void;
  removeLoadingStep: (id: string) => void;
  isLoading: boolean;
}

const LoadingContext = createContext<LoadingContextValue | undefined>(undefined);

export function LoadingProvider({ children }: { children: ReactNode }) {
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>([]);
  const [visibleMessages, setVisibleMessages] = useState<string[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);

  const addLoadingStep = useCallback((id: string, message: string) => {
    setLoadingSteps(prev => {
      // Check if step already exists
      const exists = prev.find(step => step.id === id);
      if (exists) {
        return prev;
      }
      return [...prev, { id, message, isActive: true }];
    });
  }, []);

  const removeLoadingStep = useCallback((id: string) => {
    setLoadingSteps(prev => prev.filter(step => step.id !== id));
  }, []);

  const isLoading = loadingSteps.length > 0;
  const activeMessages = loadingSteps.filter(step => step.isActive).map(step => step.message);
  const visibleMessageKey = activeMessages.join('|');
  const shouldShowLoadingScreen = visibleMessages.length > 0;

  // Track whether the screen was visible without adding visibleMessages to effect deps
  // (which caused an extra re-run every time setVisibleMessages was called).
  const wasShowingRef = useRef(false);

  useEffect(() => {
    if (activeMessages.length > 0) {
      wasShowingRef.current = true;
      setVisibleMessages(activeMessages);
      setIsCompleting(false);
      return;
    }

    if (!wasShowingRef.current) return;
    wasShowingRef.current = false;

    setIsCompleting(true);

    const timeout = setTimeout(() => {
      setVisibleMessages([]);
      setIsCompleting(false);
    }, 550);

    return () => clearTimeout(timeout);
  }, [activeMessages.length, visibleMessageKey]);

  return (
    <LoadingContext.Provider value={{ addLoadingStep, removeLoadingStep, isLoading }}>
      {children}
      {shouldShowLoadingScreen && (
        <UnifiedLoadingScreen messages={visibleMessages} isCompleting={isCompleting} />
      )}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
}
